import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Gem, Check, Sparkles, Clock, PlayCircle, Brain, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import { Confetti } from "@/components/Fx";

function fmt(s) {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

export default function Store() {
  const { setCharacter } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(null);
  const [adPhase, setAdPhase] = useState(null);
  const [burst, setBurst] = useState(0);

  const load = useCallback(async () => {
    try {
      const [c, q] = await Promise.all([api.get("/gems/center"), api.get("/gems/quiz")]);
      setData(c.data); setQuiz(q.data); setPicked(null);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // local countdown tick — start once data has loaded
  const hasData = data !== null;
  useEffect(() => {
    if (!hasData) return;
    const t = setInterval(() => {
      setData((d) => d && ({
        ...d,
        time_bonus: { ...d.time_bonus, seconds_left: Math.max(0, d.time_bonus.seconds_left - 1), ready: d.time_bonus.seconds_left - 1 <= 0 },
        quiz: { ...d.quiz, seconds_left: Math.max(0, d.quiz.seconds_left - 1), ready: d.quiz.seconds_left - 1 <= 0 },
      }));
      setQuiz((q) => q && ({ ...q, seconds_left: Math.max(0, q.seconds_left - 1), ready: q.seconds_left - 1 <= 0 }));
    }, 1000);
    return () => clearInterval(t);
  }, [hasData]);

  const claimTime = async () => {
    setBusy("time");
    try {
      const { data: r } = await api.post("/gems/time-bonus");
      setCharacter(r.character); setBurst((b) => b + 1);
      toast.success(`+${r.gems_awarded} 💎 free gems!`, { description: "Come back in 30 min for more." });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const watchAd = async () => {
    setAdPhase(4);
    const tick = setInterval(() => setAdPhase((n) => (n > 1 ? n - 1 : n)), 1000);
    setTimeout(async () => {
      clearInterval(tick);
      try {
        const { data: r } = await api.post("/rewards/ad");
        setCharacter(r.character); setBurst((b) => b + 1);
        toast.success(`Bonus! +${naira(r.reward.cash)}${r.reward.gems ? ` +${r.reward.gems}💎` : ""}`, { description: `${r.ads_left} free bonuses left today` });
        load();
      } catch (e) { toast.error(errMsg(e)); } finally { setAdPhase(null); }
    }, 4000);
  };

  const answerQuiz = async (choice) => {
    setPicked(choice); setBusy("quiz");
    try {
      const { data: r } = await api.post("/gems/quiz/answer", { quiz_id: quiz.quiz.id, choice });
      setCharacter(r.character);
      if (r.correct) { setBurst((b) => b + 1); toast.success(`Correct! +${r.gems_awarded} 💎`, { description: "Sharp brain! Next test in 20 min." }); }
      else toast.error("Not quite 😅", { description: "No gems this time — try the next one." });
      setTimeout(load, 1400);
    } catch (e) { toast.error(errMsg(e)); setPicked(null); } finally { setBusy(null); }
  };

  const buyPremium = async (itemId, title) => {
    setBusy(itemId);
    try {
      const { data: r } = await api.post("/store/buy-premium", { item_id: itemId });
      setCharacter(r.character);
      toast.success(`Unlocked ${title} ✨`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) return null;
  const owned = new Set(data.owned);

  return (
    <div className="px-5 pt-6" data-testid="store-page">
      <Confetti burst={burst} />
      <div className="flex items-center justify-between">
        <div>
          <span className="label-xs text-[#16A34A]">100% FREE TO PLAY</span>
          <h1 className="font-display text-3xl mt-1">Free <span className="text-[#FFD700]">Gems</span></h1>
        </div>
        <div className="flex items-center gap-1 text-[#FFD700] font-display text-xl" data-testid="gem-balance"><Gem size={18} /> {data.gems}</div>
      </div>

      <div className="mt-2 bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-800">
        This game is <b>100% free</b> — you never pay a kobo. Gems are earned by playing: grab your time bonus, watch a quick bonus, and ace the Naija quiz. 🎁
      </div>

      {/* Time bonus */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="time-bonus-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#FFD700]/10 flex items-center justify-center"><Clock size={20} className="text-[#FFD700]" /></div>
          <div className="flex-1">
            <div className="font-semibold">Time Bonus</div>
            <div className="label-xs text-slate-500">+{data.time_bonus.amount} 💎 every 30 min you play</div>
          </div>
          <button data-testid="claim-time-bonus" disabled={!data.time_bonus.ready || busy === "time"} onClick={claimTime}
            className="px-4 py-2.5 rounded-xl font-bold label-xs bg-[#FFD700] text-slate-900 disabled:opacity-40">
            {data.time_bonus.ready ? "Claim" : fmt(data.time_bonus.seconds_left)}
          </button>
        </div>
      </div>

      {/* Watch bonus */}
      <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4" data-testid="watch-bonus-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#16A34A]/10 flex items-center justify-center"><PlayCircle size={20} className="text-[#16A34A]" /></div>
          <div className="flex-1">
            <div className="font-semibold">Watch a Short Bonus</div>
            <div className="label-xs text-slate-500">Cash + a chance at gems · {data.ad_bonus.left} left today</div>
          </div>
          <button data-testid="watch-bonus-btn" disabled={data.ad_bonus.left <= 0 || data.ad_bonus.seconds_left > 0 || adPhase !== null} onClick={watchAd}
            className="px-4 py-2.5 rounded-xl font-bold label-xs bg-[#16A34A] text-white disabled:opacity-40">
            {data.ad_bonus.seconds_left > 0 ? fmt(data.ad_bonus.seconds_left) : "Watch"}
          </button>
        </div>
      </div>

      {/* Naija quiz */}
      <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4" data-testid="quiz-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center"><Brain size={20} className="text-[#00E5FF]" /></div>
          <div className="flex-1">
            <div className="font-semibold">Naija Brain Test</div>
            <div className="label-xs text-slate-500">Answer right, earn +{quiz?.reward} 💎 · every 20 min</div>
          </div>
        </div>
        {quiz && (
          quiz.ready ? (
            <div className="mt-3">
              <p className="text-sm text-slate-900">{quiz.quiz.q}</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {quiz.quiz.options.map((opt, i) => (
                  <button key={opt} data-testid={`quiz-opt-${i}`} disabled={picked !== null} onClick={() => answerQuiz(i)}
                    className={`text-left px-3 py-2.5 rounded-lg label-xs border transition-colors ${picked === i ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 bg-slate-100 hover:border-[#00E5FF]/50"}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="mt-3 label-xs text-slate-500">Next brain-test in {fmt(quiz.seconds_left)}. Sharpen up! 🧠</p>
          )
        )}
      </div>

      <button data-testid="store-advertise" onClick={() => nav("/advertise")} className="mt-4 w-full bg-amber-50 border border-amber-300 text-amber-700 rounded-xl py-3 label-xs flex items-center justify-center gap-2 font-bold">
        📣 Got a business? Advertise in the city
      </button>

      {/* Cosmetics — spend earned gems */}
      <span className="label-xs text-slate-500 mt-7 block">Spend Your Gems · Cosmetics</span>
      <div className="mt-3 space-y-2 mb-4">
        {data.cosmetics.map((cos) => {
          const Icon = Icons[cos.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Sparkles;
          const has = owned.has(cos.id);
          const afford = data.gems >= cos.gems;
          return (
            <div key={cos.id} data-testid={`cosmetic-${cos.id}`} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-black/60 flex items-center justify-center"><Icon size={18} className="text-[#FF0055]" /></div>
              <div className="flex-1"><div className="font-semibold text-sm">{cos.title}</div><div className="label-xs text-[#FFD700] flex items-center gap-1 mt-0.5"><Gem size={11} /> {cos.gems}</div></div>
              {has ? (
                <span className="label-xs text-[#16A34A] flex items-center gap-1"><Check size={14} /> Owned</span>
              ) : (
                <button data-testid={`buy-cos-${cos.id}`} onClick={() => buyPremium(cos.id, cos.title)} disabled={!afford || busy === cos.id}
                  className={`px-4 py-2 rounded-lg label-xs transition-colors ${afford ? "bg-[#FF0055] text-white hover:bg-[#ff2e73]" : "bg-slate-200 text-slate-400"}`}>
                  {busy === cos.id ? "…" : afford ? "Unlock" : "Need gems"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* watch-bonus overlay */}
      <AnimatePresence>
        {adPhase !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center px-6" data-testid="watch-overlay">
            <div className="absolute top-4 right-4 label-xs text-slate-500">Sponsored · demo</div>
            <div className="w-full max-w-xs aspect-square rounded-2xl bg-gradient-to-br from-[#FFD700] via-[#16A34A] to-[#00E5FF] flex flex-col items-center justify-center text-center p-6">
              <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1, repeat: Infinity }} className="text-6xl">🎁</motion.div>
              <div className="font-display text-2xl text-slate-900 mt-4">Free Bonus</div>
            </div>
            <div className="mt-6 label-xs text-slate-500">Reward in {adPhase}s…</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
